import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, CalendarDays, Check, Clock, Info, MapPin, Minus, Plus, Users, X } from "lucide-react";
import { PageDecor } from "../components/decor/PageDecor";
import RetourApp from "../components/RetourApp";
import { useSEO } from "../lib/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* « Modifier ou annuler ma réservation » : le lien personnel des e-mails du spectacle de Noël
   (Jen, 02.11.2026 : « ils doivent pouvoir modifier et retirer des billets »). Sans compte,
   comme /mon-essai : baisser le nombre se fait toujours, monter seulement dans les places
   libres ; annuler libère la place pour la liste d'attente, qui tourne aussitôt. L'école est
   prévenue par e-mail, la console est déjà à jour. */

async function lire(r) {
  let d = {};
  try { d = await r.json(); } catch { d = {}; }
  if (!r.ok) throw new Error(typeof d.detail === "string" && d.detail ? d.detail : `Erreur ${r.status}`);
  return d;
}

const personnes = (n) => `${n} personne${n > 1 ? "s" : ""}`;
const rangTexte = (n) => (n === 1 ? "1re" : `${n}e`);
const ROUGE = { background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" };
const VERT = { background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" };
const AMBRE = { background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" };

function Reservation({ resa, jeton, onChange }) {
  const [mode, setMode] = useState("");
  const [n, setN] = useState(resa.nb_personnes);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState("");
  const [fait, setFait] = useState("");

  const attente = resa.etat === "attente";
  const annulee = resa.etat === "annulee";
  const modifiable = !resa.passe && !annulee;
  const tropHaut = n > resa.max_possible;

  const agir = async (action, corps) => {
    setEnCours(true);
    setErreur("");
    try {
      const d = await lire(await fetch(`${API}/spectacle/gerer/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jeton, ...corps }),
      }));
      const r = d.reservation;
      onChange(r);
      const exemple = d.exemple ? " (Exemple : rien n'est enregistré.)" : "";
      if (action === "annuler") {
        setFait((attente
          ? "C'est noté : ta demande est retirée de la liste d'attente. Merci de nous avoir prévenus !"
          : Number(r?.nb_personnes || 0) > 1
            ? "C'est noté, merci d'avoir libéré vos places : d'autres familles pourront venir."
            : "C'est noté, merci d'avoir libéré ta place : une autre famille pourra venir.") + exemple);
      } else if (d.promu) {
        setFait(`Bonne nouvelle : une place s'est libérée, ta réservation est confirmée pour ${personnes(r.nb_personnes)} ! Tu reçois un e-mail de confirmation.`);
      } else {
        setFait(`C'est noté : ${r.etat === "attente" ? "ta demande en liste d'attente" : "ta réservation"} est maintenant de ${personnes(r.nb_personnes)}. L'école est prévenue, et tu reçois un e-mail de confirmation.${exemple}`);
      }
      setMode("");
      setN(r.nb_personnes);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnCours(false);
    }
  };

  const ouvrir = (m) => { setMode(m); setN(resa.nb_personnes); setFait(""); setErreur(""); };

  return (
    <div className="rounded-3xl p-6 mt-8" data-testid="mon-spectacle-reservation"
      style={{ background: "#fff", border: "1.5px solid rgba(15,27,45,0.10)", opacity: annulee && !fait ? 0.8 : 1 }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-bold tracking-widest uppercase" style={{ color: "#0E7A85" }}>
            {resa.prenom || resa.nom ? `Réservation de ${[resa.prenom, resa.nom].filter(Boolean).join(" ")}` : "Ta réservation"}
          </div>
          <div className="font-display text-2xl tracking-tight mt-1">{resa.spectacle}</div>
        </div>
        <span className="shrink-0 text-xs font-semibold rounded-full px-3 py-1" data-testid="mon-spectacle-etat"
          style={annulee ? { background: "#FEE2E2", color: "#B91C1C" } : attente
            ? { background: "#FEF3C7", color: "#92400E" } : { background: "#DCFCE7", color: "#166534" }}>
          {annulee ? "Annulée" : attente ? "Liste d'attente" : "Confirmée"}
        </span>
      </div>

      <div className="mt-4 space-y-1.5 text-sm" style={{ textDecoration: annulee ? "line-through" : "none" }}>
        <div className="flex items-center gap-2"><CalendarDays size={15} className="shrink-0" /> <strong>{resa.date_label}</strong></div>
        {resa.heure && <div className="flex items-center gap-2 text-[var(--muted)]"><Clock size={15} className="shrink-0" /> {resa.heure}</div>}
        {resa.lieu && <div className="flex items-center gap-2 text-[var(--muted)]"><MapPin size={15} className="shrink-0" /> {resa.lieu}</div>}
        <div className="flex items-center gap-2"><Users size={15} className="shrink-0" />
          <span data-testid="mon-spectacle-nombre"><strong>{personnes(resa.nb_personnes)}</strong>{attente ? " (en attente d'une place)" : ""}</span>
        </div>
      </div>

      {attente && !fait && (
        <div className="rounded-2xl px-4 py-3 text-sm mt-4 flex gap-2" style={AMBRE} data-testid="mon-spectacle-attente">
          <Info size={16} className="shrink-0 mt-0.5" />
          <span>
            {resa.rang ? <>Ta demande est la <strong>{rangTexte(resa.rang)}</strong> de la liste d'attente. </> : null}
            Premier arrivé, premier servi : dès qu'une place se libère, nous confirmons les demandes dans l'ordre
            d'arrivée et tu reçois un e-mail.
          </span>
        </div>
      )}

      {fait && (
        <div className="rounded-2xl px-4 py-3 text-sm mt-4 flex gap-2" style={VERT} data-testid="mon-spectacle-fait">
          <Check size={16} className="shrink-0 mt-0.5" /> {fait}
        </div>
      )}
      {erreur && (
        <div className="rounded-2xl px-4 py-3 text-sm mt-4 flex gap-2" style={ROUGE} data-testid="mon-spectacle-erreur">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {erreur}
        </div>
      )}

      {resa.passe && (
        <p className="text-sm text-[var(--muted)] mt-4" data-testid="mon-spectacle-passe">
          Le spectacle a eu lieu ({resa.date_label}) : cette réservation ne se change plus.
          {!annulee && " Merci d'être venus !"}
        </p>
      )}
      {annulee && !resa.passe && !fait && (
        <p className="text-sm text-[var(--muted)] mt-4" data-testid="mon-spectacle-annulee">
          Cette réservation est annulée. Envie de venir quand même ? Réserve à nouveau sur{" "}
          <Link to="/inscription/spectacle" className="underline hover:text-[var(--primary)]">la page du spectacle</Link>,
          s'il reste des places.
        </p>
      )}

      {modifiable && !mode && (
        <div className="flex flex-wrap gap-3 mt-5">
          <button type="button" className="btn-primary" onClick={() => ouvrir("nombre")} data-testid="mon-spectacle-changer">
            <Users size={16} /> Changer le nombre de personnes
          </button>
          <button type="button" className="btn-ghost" onClick={() => ouvrir("annuler")} data-testid="mon-spectacle-annuler">
            <X size={16} /> {attente ? "Me retirer de la liste d'attente" : "Annuler ma réservation"}
          </button>
        </div>
      )}

      {modifiable && mode === "nombre" && (
        <div className="mt-6" data-testid="mon-spectacle-choix">
          <p className="text-sm text-[var(--muted)]">Combien serez-vous ?</p>
          <div className="flex items-center gap-5 mt-3">
            <button type="button" className="w-12 h-12 rounded-full flex items-center justify-center transition disabled:opacity-30"
              style={{ border: "1.5px solid rgba(15,27,45,0.15)" }} disabled={n <= 1} onClick={() => setN(n - 1)}
              aria-label="Une personne de moins" data-testid="mon-spectacle-moins"><Minus size={18} /></button>
            <div className="text-center min-w-[5rem]">
              <div className="font-display text-5xl leading-none" data-testid="mon-spectacle-n">{n}</div>
              <div className="text-xs text-[var(--muted)] mt-1">{n > 1 ? "personnes" : "personne"}</div>
            </div>
            <button type="button" className="w-12 h-12 rounded-full flex items-center justify-center transition disabled:opacity-30"
              style={{ border: "1.5px solid rgba(15,27,45,0.15)" }} disabled={n >= resa.max_par_reservation} onClick={() => setN(n + 1)}
              aria-label="Une personne de plus" data-testid="mon-spectacle-plus"><Plus size={18} /></button>
          </div>
          {tropHaut ? (
            <div className="rounded-2xl px-4 py-3 text-sm mt-4 flex gap-2" style={AMBRE} data-testid="mon-spectacle-complet">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>
                <strong>C'est complet</strong> : {resa.libres > 0
                  ? `il ne reste que ${resa.libres} place${resa.libres > 1 ? "s" : ""} libre${resa.libres > 1 ? "s" : ""}, pas assez pour passer à ${n} personnes.`
                  : "il n'y a plus de place libre pour l'instant."}{" "}
                Ta réservation de {personnes(resa.nb_personnes)} reste valable.
              </span>
            </div>
          ) : n < resa.nb_personnes && !attente ? (
            <p className="text-sm mt-4" style={{ color: "#15803D" }}>
              Merci ! {n === resa.nb_personnes - 1 ? "La place libérée ira" : "Les places libérées iront"} à une famille de la liste d'attente.
            </p>
          ) : attente ? (
            <p className="text-sm text-[var(--muted)] mt-4">Ta demande garde sa place dans la liste d'attente.</p>
          ) : null}
          <div className="flex flex-wrap gap-3 mt-5">
            <button type="button" className="btn-primary" disabled={enCours || n === resa.nb_personnes || tropHaut}
              onClick={() => agir("nombre", { nb_personnes: n })} data-testid="mon-spectacle-enregistrer">
              {enCours ? "Un instant…" : `Enregistrer ${personnes(n)}`}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setMode("")}>Retour</button>
          </div>
        </div>
      )}

      {modifiable && mode === "annuler" && (
        <div className="mt-6" data-testid="mon-spectacle-annulation">
          <p className="text-sm">
            {attente
              ? `Ta demande de ${personnes(resa.nb_personnes)} sort de la liste d'attente. Tu es sûr·e ?`
              : `Tu libères ${resa.nb_personnes > 1 ? `${resa.nb_personnes} places` : "ta place"} : une autre famille pourra venir. Tu es sûr·e ?`}
          </p>
          <div className="flex flex-wrap gap-3 mt-4">
            <button type="button" className="btn-primary" disabled={enCours} style={{ background: "#B91C1C", color: "#fff" }}
              onClick={() => agir("annuler", {})} data-testid="mon-spectacle-confirmer-annulation">
              {enCours ? "Un instant…" : attente ? "Oui, me retirer de la liste" : "Oui, annuler ma réservation"}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setMode("")}>Retour</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MonSpectacle() {
  // Page personnelle : jamais indexée.
  useSEO({ title: "Ma réservation — Spectacle", description: "Modifier ou annuler ta réservation au spectacle de 1000 ARTS.", robots: "noindex, nofollow" });
  const [params] = useSearchParams();
  const jeton = params.get("jeton") || "";
  const [resa, setResa] = useState(null);
  const [erreur, setErreur] = useState("");

  useEffect(() => {
    if (!jeton) {
      setErreur("Ouvre cette page depuis le lien « Modifier ou annuler ma réservation » de ton e-mail.");
      return;
    }
    fetch(`${API}/spectacle/gerer?jeton=${encodeURIComponent(jeton)}`)
      .then(lire)
      .then((d) => setResa(d.reservation))
      .catch((e) => setErreur(e.message));
  }, [jeton]);

  return (
    <div className="pt-28 pb-24 relative overflow-hidden" data-testid="page-mon-spectacle">
      <PageDecor />
      <div className="max-w-2xl mx-auto px-6 relative">
        <RetourApp />
        <div className="eyebrow mb-3">{resa?.spectacle || "Spectacle"}</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[0.95]">
          Ma réservation<br /><span style={{ color: "var(--primary)" }}>modifier ou annuler</span>
        </h1>
        <p className="text-base text-[var(--muted)] mt-5 leading-relaxed">
          Un empêchement, ou vous serez moins nombreux ? Change le nombre de personnes ou annule ta
          réservation : la place libérée ira à une famille de la liste d'attente. L'école est prévenue
          automatiquement.
        </p>

        {resa?.exemple && (
          <div className="rounded-2xl px-4 py-3 text-sm mt-6 flex gap-2" style={AMBRE} data-testid="mon-spectacle-exemple">
            <Info size={16} className="shrink-0 mt-0.5" /> Exemple : cette réservation est fictive, rien n'est enregistré.
          </div>
        )}
        {erreur && (
          <div className="rounded-2xl px-4 py-3 text-sm mt-8 flex gap-2" style={ROUGE} data-testid="mon-spectacle-erreur-page">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {erreur}
          </div>
        )}
        {!erreur && resa === null && <p className="text-[var(--muted)] mt-8">Chargement…</p>}
        {resa && <Reservation resa={resa} jeton={jeton} onChange={setResa} />}

        <p className="text-sm text-[var(--muted)] mt-10 leading-relaxed">
          Une question ? Écris-nous à{" "}
          <a href="mailto:info1000arts@gmail.com" className="underline hover:text-[var(--primary)]">info1000arts@gmail.com</a>{" "}
          ou appelle le +41 79 950 40 31.
        </p>
        <Link to="/" className="btn-ghost mt-6 inline-flex">Retour à l'accueil</Link>
      </div>
    </div>
  );
}
